import React, { useState } from 'react';
import { PageBanner } from '../components/PageBanner';
import { ContactCTA } from '../components/ContactCTA';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import { DollarSignIcon, ShieldIcon, BuildingIcon, HeartPulseIcon, ChevronDownIcon, CheckCircleIcon, AlertCircleIcon, ExternalLinkIcon, BadgeDollarSignIcon } from 'lucide-react';
const fundingOptions = [{
  title: 'VA Aid & Attendance Benefit',
  icon: ShieldIcon,
  content: ["Veterans may qualify for Veterans Administration benefits to cover some of the cost of in-home care. A veteran who served at least 90 days on active duty with one day during a period of war may be eligible for the Veterans Administration's non-service connected disability pension."],
  extra: {
    title: 'Paying Out of Pocket for Long-Term Care',
    text: 'Seniors and their families often must tap into savings and assets to pay for long-term care, as many cannot afford long-term care insurance, many do not qualify for Medicaid, and Medicare coverage is very limited.'
  },
  bullets: []
}, {
  title: 'Long Term Care Insurance',
  icon: BadgeDollarSignIcon,
  content: ['Long-term care insurance can help seniors pay the costs of long-term care, helping to avoid depletion of life savings for extended care. However, coverage is expensive. Premiums increase the older you are when you buy it. The Kaiser Family Foundation reports that policies purchased at age 65 average $1,800 a year for four years of comprehensive coverage and $5,900 a year when purchased at age 79.', 'For those earning ample income, premiums can be affordable, but you need to make sure you will still be able to pay the premium after you retire. And even if you can, you may not be able to justify the expense if you have the resources to pay for care out of pocket. Your financial planner can help guide you in this decision.'],
  extra: {
    title: 'Important Note',
    text: 'Not all long-term insurance policies are created equal, so be sure you know what you are buying. Some policies cover only specific types of long-term care, while others will cover a variety, such as nursing home, assisted living or in-home care.'
  },
  bullets: []
}, {
  title: 'Medicaid & State Programs',
  icon: BuildingIcon,
  content: ['Medicaid, the largest public payer for long-term care, will (if you qualify), pay for nursing home care and other costs that Medicare does not cover. It may also cover some in-home care services.'],
  extra: null,
  bullets: ['Those who receive Supplemental Security Income (SSI) are likely to qualify for Medicaid automatically.', 'You also can qualify if you have limited income and assets. Eligibility guidelines vary by state.', 'Assets considered for eligibility generally do not include homes, cars, household furnishings, or burial plots.', "In some cases you may still qualify if your income exceeds your state's eligibility level.", 'In several states, seniors may qualify for Medicaid after they have spent their income and assets on nursing home or other health care expenses. This is commonly referred to as the Medicaid "spend down."'],
  afterBullets: 'Older adults who do not qualify for larger government programs like Medicaid may find they qualify for state programs. Information on these can be found through local Area Agencies on Aging.'
}, {
  title: 'Medicare',
  icon: HeartPulseIcon,
  content: ['As previously mentioned, Medicare applies to long-term care in limited circumstances, such as immediately following a hospital stay.'],
  extra: {
    title: 'Important',
    text: 'Medicare does not pay for extended care.'
  },
  bullets: [],
  coverageList: ['Short-term, physician-prescribed home health care for skilled nursing, physical, occupational and speech therapy, and medical social services', 'Assistance with bathing, feeding and other personal care by a home health aide', 'Up to 100 days of nursing home care in limited instances', 'Some durable medical equipment for home use', 'Hospice care for people diagnosed with terminal illness and a life expectancy of six months or less']
}];
export function PlanningFundingPage() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  const {
    ref: introRef,
    isVisible: isIntroVisible
  } = useScrollAnimation();
  const {
    ref: optionsRef,
    isVisible: isOptionsVisible
  } = useScrollAnimation();
  const {
    ref: ctaRef,
    isVisible: isCtaVisible
  } = useScrollAnimation();
  const toggleOption = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };
  return <main data-id="element-1076">
      <PageBanner title="Planning & Funding Care" subtitle="Understanding your options for long-term care funding and financial planning." breadcrumbs={[{
      label: 'Services',
      path: '/services'
    }, {
      label: 'Planning & Funding Care',
      path: '/services/planning-funding'
    }]} data-id="element-1077" />

      {/* Introduction */}
      <section className="relative py-16 md:py-20 bg-light" data-id="element-1078">
        <div className="absolute inset-0 bg-gradient-to-b from-primary/10 to-transparent pointer-events-none -z-10" data-id="element-1079"></div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1080">
          <div ref={introRef} className={`transition-all duration-1000 ease-out ${isIntroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`} data-id="element-1081">
            <div className="inline-flex items-center space-x-2 bg-primary/10 rounded-full px-4 py-2 mb-6" data-id="element-1082">
              <DollarSignIcon className="h-4 w-4 text-primary" data-id="element-1083" />
              <span className="text-primary text-sm font-semibold" data-id="element-1084">
                Financial Planning
              </span>
            </div>

            <div className="space-y-6 text-lg text-dark-muted leading-relaxed" data-id="element-1085">
              <p data-id="element-1086">
                The cost of long-term care for seniors varies as much as the
                types of care available and the degree of assistance you or a
                loved one need. Below is a review of some of the more common
                funding options for senior housing and long-term care services.
                Senior care can range from a few hours a week of personal or
                companionship care by an in-home care provider, to full-time
                in-home care, to skilled nursing care in a nursing home.
              </p>
              <p data-id="element-1087">
                Many who have not done their research mistakenly believe that
                Medicare can be the answer for long-term care. In reality, it
                covers only a very narrow range of long-term care costs. It is
                important that you understand ahead of time the benefits and
                limits of Medicare so you can adequately prepare for long-term
                care needs.
              </p>
              <p data-id="element-1088">
                According to the{' '}
                <a href="http://www.kff.org/medicare/8110.cfm" target="_blank" rel="noopener noreferrer" className="text-primary font-semibold hover:underline inline-flex items-center" data-id="element-1089">
                  Henry J. Kaiser Family Foundation
                  <ExternalLinkIcon className="h-3.5 w-3.5 ml-1" data-id="element-1090" />
                </a>
                , nursing home rates vary widely, but can exceed $5,000 a month.
                In-home care is generally far less expensive, but costs vary
                greatly, depending on where you live, the level of care and
                number of hours of services needed per week.
              </p>
            </div>

            {/* Key Question */}
            <div className="mt-10 bg-primary/5 border border-primary/10 rounded-2xl p-8" data-id="element-1091">
              <h3 className="text-2xl font-bold text-dark mb-4" data-id="element-1092">
                How will you pay for long-term care?
              </h3>
              <p className="text-dark-muted text-lg" data-id="element-1093">
                Options include Medicaid (if you qualify), long-term care
                insurance, Veterans Administration benefits, and out of pocket —
                from your savings and assets.
              </p>
              <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3" data-id="element-1094">
                {fundingOptions.map((opt, idx) => <button key={idx} onClick={() => {
                setOpenIndex(idx);
                document.getElementById('funding-options')?.scrollIntoView({
                  behavior: 'smooth'
                });
              }} className="bg-light rounded-xl p-4 border border-gray-200 shadow-sm hover:shadow-card hover:border-primary/20 transition-all text-center group" data-id="element-1095">
                    <opt.icon className="h-6 w-6 text-primary mx-auto mb-2 group-hover:scale-110 transition-transform" data-id="element-1096" />
                    <span className="text-xs font-semibold text-dark" data-id="element-1097">
                      {opt.title}
                    </span>
                  </button>)}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Funding Options Accordion */}
      <section id="funding-options" className="relative py-16 md:py-20 bg-light-alt" data-id="element-1098">
        <div className="absolute inset-0 bg-gradient-to-t from-primary/10 to-transparent pointer-events-none -z-10" data-id="element-1099"></div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1100">
          <div ref={optionsRef} className={`transition-all duration-1000 ease-out ${isOptionsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`} data-id="element-1101">
            <div className="text-center mb-12" data-id="element-1102">
              <h2 className="text-3xl md:text-4xl font-bold text-dark mb-4" data-id="element-1103">
                Funding Options
              </h2>
              <div className="w-16 h-1.5 bg-secondary mx-auto mb-6 rounded-full" data-id="element-1104"></div>
              <p className="text-lg text-dark-muted" data-id="element-1105">
                Explore the different ways to fund long-term care
              </p>
            </div>

            <div className="space-y-4" data-id="element-1106">
              {fundingOptions.map((option, idx) => {
              const isOpen = openIndex === idx;
              return <div key={idx} className={`bg-light rounded-xl border transition-all duration-300 overflow-hidden ${isOpen ? 'shadow-card border-primary/30' : 'shadow-sm border-gray-200 hover:border-gray-300 hover:shadow-md'}`} data-id="element-1107">
                    <button onClick={() => toggleOption(idx)} className="w-full px-6 py-5 flex items-center justify-between focus:outline-none text-left" aria-expanded={isOpen} data-id="element-1108">
                      <div className="flex items-center pr-4" data-id="element-1109">
                        <div className={`p-3 rounded-xl mr-4 flex-shrink-0 transition-colors ${isOpen ? 'bg-primary text-white' : 'bg-primary/10 text-primary'}`} data-id="element-1110">
                          <option.icon className="h-6 w-6" data-id="element-1111" />
                        </div>
                        <h3 className={`text-lg font-bold transition-colors ${isOpen ? 'text-primary' : 'text-dark'}`} data-id="element-1112">
                          {option.title}
                        </h3>
                      </div>
                      <div className={`p-2 rounded-full transition-colors ${isOpen ? 'bg-primary/10' : 'bg-gray-50'}`} data-id="element-1113">
                        <ChevronDownIcon className={`h-5 w-5 text-primary transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} data-id="element-1114" />
                      </div>
                    </button>

                    <div className={`transition-all duration-300 ease-in-out ${isOpen ? 'max-h-[1200px] opacity-100 border-t border-gray-100' : 'max-h-0 opacity-0'}`} data-id="element-1115">
                      <div className="p-6 md:p-8 space-y-5" data-id="element-1116">
                        {option.content.map((para, pIdx) => <p key={pIdx} className="text-dark-muted leading-relaxed" data-id="element-1117">
                            {para}
                          </p>)}

                        {/* Coverage list for Medicare */}
                        {'coverageList' in option && option.coverageList && <div data-id="element-1118">
                            <h4 className="font-bold text-dark mb-3" data-id="element-1119">
                              What Medicare Covers:
                            </h4>
                            <ul className="space-y-2" data-id="element-1120">
                              {option.coverageList.map((item: string, cIdx: number) => <li key={cIdx} className="flex items-start text-dark-muted" data-id="element-1121">
                                    <CheckCircleIcon className="h-5 w-5 text-secondary mr-3 flex-shrink-0 mt-0.5" data-id="element-1122" />
                                    {item}
                                  </li>)}
                            </ul>
                          </div>}

                        {/* Bullet points for Medicaid */}
                        {option.bullets && option.bullets.length > 0 && <ul className="space-y-2" data-id="element-1123">
                            {option.bullets.map((bullet, bIdx) => <li key={bIdx} className="flex items-start text-dark-muted" data-id="element-1124">
                                <CheckCircleIcon className="h-5 w-5 text-secondary mr-3 flex-shrink-0 mt-0.5" data-id="element-1125" />
                                {bullet}
                              </li>)}
                          </ul>}

                        {'afterBullets' in option && option.afterBullets && <p className="text-dark-muted leading-relaxed" data-id="element-1126">
                            {option.afterBullets}
                          </p>}

                        {/* Extra note */}
                        {option.extra && <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 flex items-start mt-4" data-id="element-1127">
                            <AlertCircleIcon className="h-6 w-6 text-amber-500 mr-3 flex-shrink-0" data-id="element-1128" />
                            <div data-id="element-1129">
                              <h4 className="font-bold text-amber-800 mb-1" data-id="element-1130">
                                {option.extra.title}
                              </h4>
                              <p className="text-amber-800 text-sm" data-id="element-1131">
                                {option.extra.text}
                              </p>
                            </div>
                          </div>}
                      </div>
                    </div>
                  </div>;
            })}
            </div>
          </div>
        </div>
      </section>

      {/* Insurance Options Banner */}
      <section className="relative py-16 bg-light" data-id="element-1132">
        <div className="absolute inset-0 bg-gradient-to-t from-primary/10 to-transparent pointer-events-none -z-10" data-id="element-1133"></div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1134">
          <div className="bg-light rounded-2xl p-8 md:p-10 border border-gray-200 shadow-card flex flex-col md:flex-row items-center justify-between gap-6" data-id="element-1135">
            <div data-id="element-1136">
              <h3 className="text-xl font-bold text-dark mb-2" data-id="element-1137">
                Insurance Options
              </h3>
              <p className="text-dark-muted" data-id="element-1138">
                See which public, private and community programs meet your
                needs.
              </p>
            </div>
            <a href="https://finder.healthcare.gov/" target="_blank" rel="noopener noreferrer" className="whitespace-nowrap bg-primary hover:bg-primary-dark text-white font-semibold px-8 py-3.5 rounded-xl transition-colors shadow-sm flex items-center" data-id="element-1139">
              Explore Options
              <ExternalLinkIcon className="ml-2 h-4 w-4" data-id="element-1140" />
            </a>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-light" data-id="element-1141">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1142">
          <div ref={ctaRef} className={`bg-primary rounded-3xl p-10 md:p-14 text-center shadow-xl relative overflow-hidden transition-all duration-1000 ease-out ${isCtaVisible ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-12 scale-95'}`} data-id="element-1143">
            <div className="absolute inset-0 opacity-10" style={{
            backgroundImage: 'radial-gradient(#ffffff 2px, transparent 2px)',
            backgroundSize: '30px 30px'
          }} data-id="element-1144"></div>
            <div className="relative z-10" data-id="element-1145">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" style={{
              fontFamily: "'Cinzel', serif"
            }} data-id="element-1146">
                Need Help Planning Your Care?
              </h2>
              <p className="text-lg text-white/90 mb-8 max-w-2xl mx-auto" data-id="element-1147">
                Our team can help you navigate funding options and create a care
                plan that fits your budget. Contact us for a free consultation.
              </p>
              <a href="#/contact" className="inline-block bg-white text-primary-dark hover:bg-light-alt px-10 py-4 rounded-xl font-bold text-lg transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1" data-id="element-1148">
                Contact Us Today
              </a>
            </div>
          </div>
        </div>
      </section>

      <ContactCTA data-id="element-1149" />
    </main>;
}